import { useMemo } from 'react';

import { ApolloQueryResult, useQuery } from '@apollo/client';

import { ActiveChannelTypeEnum } from 'constants-channel';
import { getTenantChannelsByOrgs } from 'graphql-queries';
import { Channel } from 'application-types';

export type PropertyTenantChannel = Pick<Channel, '_id' | 'name' | 'profile' | 'type'>;
export type PropertyTenantMap = Map<string, PropertyTenantChannel[]>;

type TenantChannelsByOrgsItem = {
  tenantChannelId: string;
  propertyChannelId: string;
  name: string;
};

type TenantChannelsByOrgsData = {
  tenantChannelsByOrgsForUser?: {
    items: TenantChannelsByOrgsItem[];
  };
};

export function usePropertyTenantsByRelationship(
  userChannels: Channel[],
  enabled: boolean = true
): {
  tenantsByPropertyId: PropertyTenantMap;
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<TenantChannelsByOrgsData>>;
} {
  const { orgIds, channelById } = useMemo(() => {
    const orgs = Array.from(
      userChannels.reduce<Set<string>>((acc, channel) => {
        if (channel.type === ActiveChannelTypeEnum.Property && channel.parent?._id) {
          acc.add(channel.parent._id);
        }

        return acc;
      }, new Set())
    );

    const userChannelById = new Map<string, Channel>(userChannels.map(userChannel => [userChannel._id, userChannel]));

    return { orgIds: orgs, channelById: userChannelById };
  }, [userChannels]);
  
  const { data, loading, refetch } = useQuery<TenantChannelsByOrgsData>(
    getTenantChannelsByOrgs,
    {
      skip: !enabled || orgIds.length === 0,
      variables: { orgIds },
      fetchPolicy: 'cache-first',
    }
  );

  const tenantsByPropertyId = useMemo((): PropertyTenantMap => {
    const items: TenantChannelsByOrgsItem[] =
      data?.tenantChannelsByOrgsForUser?.items ?? [];
    const map: PropertyTenantMap = new Map();

    for (const { tenantChannelId, propertyChannelId, name } of items) {
      const tenantChannel: PropertyTenantChannel =
        channelById.get(tenantChannelId) ?? {
          _id: tenantChannelId,
          name,
          type: ActiveChannelTypeEnum.Company,
        };

      const existing = map.get(propertyChannelId) ?? [];

      existing.push(tenantChannel);
      map.set(propertyChannelId, existing);
    }

    return map;
  }, [data, channelById]);

  return { tenantsByPropertyId, loading, refetch };
}
